import React, { useState, useEffect } from "react";
import "./App.css";
import Contact from "./Contact";
import Admin from "./Admin";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy, getDoc, doc } from "firebase/firestore";

// ── Fallback designs (shown only until you add real ones in the admin) ────
const fallbackDesigns = [
  {
    id: 1,
    name: "Serpent Pendant",
    category: "Pendants",
    description: "Detailed serpent pendant designed for resin and FDM printing.",
    images: [
      "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=900&q=80",
      "https://images.unsplash.com/photo-1611244419377-b0a760c19719?w=900&q=80",
      "https://images.unsplash.com/photo-1630839437035-dac17da580d0?w=900&q=80",
    ],
    etsy: "https://etsy.com/your-listing",
    patreon: "https://patreon.com/sprykal",
  },
  {
    id: 2,
    name: "Arch Ring",
    category: "Rings",
    description: "Parametric ring, fully printable with no supports needed.",
    images: [
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=900&q=80",
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
    ],
    etsy: "https://etsy.com/your-listing-2",
    patreon: null,
  },
  {
    id: 3,
    name: "Moon Slice",
    category: "Pendants",
    description: "Layered pendant in multiple parts with a clean, precise fit.",
    images: [
      "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=900&q=80",
      "https://images.unsplash.com/photo-1634926878768-2a5b3c42f139?w=900&q=80",
      "https://images.unsplash.com/photo-1611244419377-b0a760c19719?w=900&q=80",
    ],
    etsy: null,
    patreon: "https://patreon.com/sprykal",
  },
  {
    id: 4,
    name: "Void Charm",
    category: "Charms",
    description: "Small charm with negative-space geometry. Resin-tested.",
    images: [
      "https://images.unsplash.com/photo-1630839437035-dac17da580d0?w=900&q=80",
      "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=900&q=80",
    ],
    etsy: "https://etsy.com/your-listing-4",
    patreon: "https://patreon.com/sprykal",
  },
  {
    id: 5,
    name: "Thorn Figure",
    category: "Miniatures",
    description: "Detailed miniature, modular and ready to print.",
    images: [
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=900&q=80",
    ],
    etsy: "https://etsy.com/your-listing-5",
    patreon: null,
  },
  {
    id: 6,
    name: "Flora Drop",
    category: "Pendants",
    description: "Organic pendant with a high-detail surface. Print-ready.",
    images: [
      "https://images.unsplash.com/photo-1634926878768-2a5b3c42f139?w=900&q=80",
      "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=900&q=80",
      "https://images.unsplash.com/photo-1630839437035-dac17da580d0?w=900&q=80",
    ],
    etsy: null,
    patreon: "https://patreon.com/sprykal",
  },
];

// ── Nav ───────────────────────────────────────────────────────────────────
function Nav({ page, setPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const go = (p) => { setPage(p); setOpen(false); };
  return (
    <nav className={"nav" + (scrolled ? " nav--scrolled" : "")}>
      <button className="nav__logo" onClick={() => go("home")}>Sprykal</button>
      <button className="nav__burger" onClick={() => setOpen(!open)} aria-label="menu">
        <span className={open ? "open" : ""}/><span className={open ? "open" : ""}/><span className={open ? "open" : ""}/>
      </button>
      <div className={"nav__links" + (open ? " nav__links--open" : "")}>
        {[["home","home"],["work","collection"],["contact","contact"]].map(([key,label]) => (
          <button key={key} className={"nav__link" + (page===key ? " active" : "")} onClick={() => go(key)}>{label}</button>
        ))}
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────
const DEFAULT_HERO = "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=900&q=80";

function Hero({ setPage, heroImage }) {
  return (
    <section className="hero">
      <div className="hero__left">
        <span className="hero__badge">✦ 3D Printable · STL Files</span>
        <h1 className="hero__title">Bring Your Ideas<br/>to Life in <em>3D.</em></h1>
        <p className="hero__body">Browse original STL files or commission a custom design made just for you.</p>
        <div className="hero__cta">
          <button className="btn btn--dark" onClick={() => setPage("work")}>Browse Collection</button>
          <button className="btn btn--outline" onClick={() => setPage("contact")}>Start a Project</button>
        </div>
        <p className="hero__tagline">Original designs. Made with passion.</p>
      </div>
      <div className="hero__right">
        <div className="hero__media">
          {heroImage
            ? <img src={heroImage} alt="3D pendant design"/>
            : <div className="hero__media-skeleton" />}
        </div>
      </div>
    </section>
  );
}

// ── Design panel (side drawer) ────────────────────────────────────────────
function Panel({ design, onClose }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    setActive(0);
    if (!design) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [design]);

  if (!design) return null;
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={e => e.stopPropagation()}>
        <button className="panel__close" onClick={onClose}>✕</button>

        {/* Main image */}
        <div className="panel__main">
          <img src={design.images[active]} alt={design.name}/>
        </div>

        {/* Thumbnails */}
        {design.images.length > 1 && (
          <div className="panel__thumbs">
            {design.images.map((src, i) => (
              <button
                key={i}
                className={"panel__thumb" + (active===i ? " active" : "")}
                onClick={() => setActive(i)}
              >
                <img src={src} alt={`View ${i+1}`}/>
              </button>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="panel__info">
          {design.category && <span className="panel__cat">{design.category}</span>}
          <h2 className="panel__name">{design.name}</h2>
          <p className="panel__desc">{design.description}</p>
          <div className="panel__actions">
            {design.etsy && (
              <a href={design.etsy} target="_blank" rel="noopener noreferrer" className="btn btn--dark">
                View on Etsy
              </a>
            )}
            {design.patreon && (
              <a href={design.patreon} target="_blank" rel="noopener noreferrer" className="btn btn--outline">
                Become a Patron
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Work / Gallery ────────────────────────────────────────────────────────
function Work({ setPage }) {
  const [selected, setSelected] = useState(null);
  const [designs, setDesigns] = useState(null); // null = still loading

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const q = query(collection(db, "designs"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        if (!active) return;
        if (!snap.empty) {
          setDesigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          setDesigns(fallbackDesigns);
        }
      } catch (e) {
        console.error("Could not load designs, using fallback.", e);
        if (active) setDesigns(fallbackDesigns);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="work-wrap">
      {/* Section 1 — Hero */}
      <section className="work-hero">
        <span className="hero__badge">✦ Original STL Designs</span>
        <p className="work-hero__sub">Browse my collection of original STL files. Each piece is available through Etsy or Patreon.</p>
      </section>

      {/* Gallery */}
      <div className="gallery">
        {designs === null ? (
          <p className="gallery-loading">Loading designs…</p>
        ) : (
          designs.map(d => (
            <button key={d.id} className="design-card" onClick={() => setSelected(d)}>
              <div className="design-card__img">
                <img src={d.images[0]} alt={d.name} loading="lazy"/>
              </div>
              <div className="design-card__footer">
                <span className="design-card__name">{d.name}</span>
                <span className="design-card__arrow">→</span>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Bottom CTA */}
      <div className="work-cta">
        <h2>Looking for something custom?</h2>
        <p>I also create one-of-a-kind STL designs tailored to your idea.</p>
        <button className="btn btn--dark" onClick={() => setPage("contact")}>Start a Project</button>
      </div>

      <Panel design={selected} onClose={() => setSelected(null)}/>
    </div>
  );
}

// ── Trust bar ─────────────────────────────────────────────────────────────
function TrustBar() {
  return (
    <div className="trust-bar">
      <div className="trust-item">
        <div className="trust-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4-6.3-4.6L5.7 21l2.3-7.4-6-4.6h7.6z"/></svg>
        </div>
        <strong>Original Designs</strong>
        <p>Every model is sculpted from scratch — no templates, no reuse.</p>
      </div>
      <div className="trust-item">
        <div className="trust-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        </div>
        <strong>After-Sale Support</strong>
        <p>Hit a snag with a file? Message me and I'll help you sort it.</p>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [isAdmin, setIsAdmin] = useState(window.location.hash === "#admin");
  const [page, setPage] = useState("home");
  const [settings, setSettings] = useState(null); // null = still loading

  useEffect(() => {
    const onHash = () => setIsAdmin(window.location.hash === "#admin");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "site"));
        if (active) setSettings(snap.exists() ? snap.data() : {});
      } catch (e) {
        console.error("Could not load site settings.", e);
        if (active) setSettings({});
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  if (isAdmin) return <Admin />;

  // Resolve hero image: null while loading (prevents flash), then saved or default
  const heroImage = settings === null ? null : (settings.heroImage || DEFAULT_HERO);

  // Build the page background from settings (image URL or color)
  const bg = settings && settings.background;
  const appStyle = bg
    ? (/^https?:\/\//.test(bg)
        ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }
        : { background: bg })
    : {};

  return (
    <div className="app" style={appStyle}>
      <Nav page={page} setPage={setPage}/>
      <main>
        {page === "home" && (
          <div className="home-wrap">
            <Hero setPage={setPage} heroImage={heroImage}/>
            <TrustBar/>
          </div>
        )}
        {page === "work"    && <Work setPage={setPage}/>}
        {page === "contact" && <Contact/>}
      </main>
      <footer className="footer">
        <span>© {new Date().getFullYear()} Sprykal</span>
      </footer>
    </div>
  );
}
